import { AfterContentInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DocumentsService } from '@cl/core/services/documents.service';
import { DATE_TIME_FORMAT } from '@cl/shared/constants';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { Document, EntityType, User } from '@cl/shared/models';
import { DocumentFile } from '@cl/shared/models/documents/document-file.model';
import { ESignatureSignableFileType } from '@cl/shared/models/e-signature/e-signature-signable-file-type.enum';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { DocumentFileSignersModalComponent } from './document-file-signers-modal/document-file-signers-modal.component';
import { AuthService, UsersService } from '@cl/core/services';
import { File as FileModel } from '@cl/shared/models/file.model';
import { HttpParams } from '@angular/common/http';
import { DocumentFileSigner } from '@cl/shared/models/documents/document-file-signer.model';
import { TableSorters } from '@cl/shared/table-sorters';

@Component({
    selector: 'cl-document-files-table',
    templateUrl: './document-files-table.component.html',
    styleUrl: './document-files-table.component.less',
    standalone: false,
})
export class DocumentFilesTableComponent implements OnInit, AfterContentInit {
    GroupPermissions = GroupPermissionsType;
    ESignatureSignableFileTypes = ESignatureSignableFileType;
    EntityType = EntityType;

    dateTimeFormat: string = DATE_TIME_FORMAT;

    loading: boolean = false;

    @Input() document: Document;
    @Output() documentChange: EventEmitter<Document> = new EventEmitter<Document>();

    @Input() documentIsSignable: boolean;
    @Input() canEditFields: boolean;
    @Input() isEditMode: boolean;

    allSelectedChecked: boolean = false;
    allSelectedIndeterminate: boolean = false;
    setOfCheckedFiles: Set<DocumentFile> = new Set<DocumentFile>();
    signableDocumentFiles: DocumentFile[];

    visibleColumns: string[] = [];

    constructor(
        private documentsService: DocumentsService,
        private modalService: NzModalService,
        private messageService: NzMessageService,
        private usersService: UsersService,
        private authService: AuthService,
    ) {}

    ngOnInit(): void {
        if (this.document.contractId || this.document.scontractId) {
            this.visibleColumns = ['clientSignedTimestamp', 'metrumSignedTimestamp', 'isSignableByClient', 'isSignableByMetrum'];
        } else {
            this.visibleColumns = ['metrumSignedTimestamp', 'isSignableByMetrum'];
        }
    }

    ngAfterContentInit(): void {
        this.signableDocumentFiles = this.document.documentFiles.filter((df: DocumentFile): boolean => df.isSignableByMetrum);
    }

    async isSignableCheckedChanged(documentFile: DocumentFile): Promise<void> {
        try {
            this.loading = true;
            await this.documentsService.updateDocumentFile(documentFile);
            await this.reloadDocument();
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            await this.reloadDocument();
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda labojot dokumenta failu');
        }
    }

    async editDocumentFileSigners(documentFile: DocumentFile): Promise<void> {
        try {
            this.loading = true;
            const editableDocumentFile: DocumentFile = (await this.documentsService.getDocumentFile(documentFile.documentId, documentFile.id)).data;
            if (!editableDocumentFile) {
                this.loading = false;
                return;
            }

            const users: User[] = (await this.usersService.getUsersForDocumentSigning(documentFile.documentId)).data;

            this.loading = false;

            const modal: NzModalRef<DocumentFileSignersModalComponent> = this.modalService.create({
                nzTitle: 'Dokumenta faila parakstītāji',
                nzContent: DocumentFileSignersModalComponent,
                nzWidth: 'fit-content',
            });

            modal.afterOpen.subscribe((): void => {
                modal.getContentComponent().documentFiles = [editableDocumentFile];
                modal.getContentComponent().documentFileSigners = editableDocumentFile.documentFileSigners;
                modal.getContentComponent().users = users;
            });

            modal.afterClose.subscribe(async (): Promise<void> => {
                this.loading = true;
                await this.reloadDocument();
                this.loading = false;
            });
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }
            this.messageService.error('Neizdevās labot dokumenta faila parakstītājus');
            return;
        }
    }

    async configureSelectedDocumentFileSigners(): Promise<void> {
        if (this.setOfCheckedFiles.size == 0) {
            return;
        }

        let commonDocumentFileSignersQueryParams: HttpParams = new HttpParams().append('documentId', this.document.id);
        this.setOfCheckedFiles.forEach((documentFile: DocumentFile) => {
            commonDocumentFileSignersQueryParams = commonDocumentFileSignersQueryParams.append('documentFileIds', documentFile.id);
        });

        try {
            this.loading = true;
            const commonDocumentFileSigners: DocumentFileSigner[] = (
                await this.documentsService.getCommonDocumentFileSigners(commonDocumentFileSignersQueryParams)
            ).data;

            const users: User[] = (await this.usersService.getUsersForDocumentSigning(this.document.id)).data;

            this.loading = false;

            const modal: NzModalRef<DocumentFileSignersModalComponent> = this.modalService.create({
                nzTitle: 'Dokumenta failu parakstītāji',
                nzContent: DocumentFileSignersModalComponent,
                nzWidth: 'fit-content',
            });

            modal.afterOpen.subscribe((): void => {
                modal.getContentComponent().documentFiles = Array.from(this.setOfCheckedFiles.values());
                modal.getContentComponent().documentFileSigners = commonDocumentFileSigners;
                modal.getContentComponent().users = users;
                modal.getContentComponent().multiDocumentFileMode = true;
            });

            modal.afterClose.subscribe(async (): Promise<void> => {
                this.loading = true;
                await this.reloadDocument();
                this.loading = false;
            });
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda dzēšot dokumenta failu');
        }
    }

    async deleteDocumentFile(documentFile: DocumentFile, documentFileIndex: number): Promise<void> {
        try {
            this.loading = true;
            await this.documentsService.deleteDocumentFile(this.document.id, documentFile.id);
            this.document.documentFiles.splice(documentFileIndex, 1);
            this.document.documentFiles = [...this.document.documentFiles];
            this.signableDocumentFiles = this.document.documentFiles.filter((df: DocumentFile): boolean => df.isSignableByMetrum);
            this.messageService.success('Dokumenta fails dzēsts');
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda dzēšot dokumenta failu');
        }
    }

    async downloadDocumentFile(documentFile: DocumentFile): Promise<void> {
        try {
            this.loading = true;
            const downloadDocumentFileParams = new HttpParams()
                .append('documentId', documentFile.documentId)
                .append('documentFileId', documentFile.id);
            await this.documentsService.downloadDocumentFile(downloadDocumentFileParams);
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda lejupielādējot failu');
        }
    }

    async downloadAll(): Promise<void> {
        try {
            this.loading = true;
            await this.documentsService.downloadAllDocumentFiles(this.document.id);
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda lejupielādējot failus');
        }
    }

    async reloadDocument(): Promise<void> {
        try {
            this.loading = true;
            this.document = (await this.documentsService.getSingle(this.document.id)).data;
            this.documentChange.emit(this.document);
            this.setOfCheckedFiles = new Set<DocumentFile>();
            this.allSelectedChecked = false;
            this.allSelectedIndeterminate = false;
            this.signableDocumentFiles = this.document.documentFiles.filter((df: DocumentFile): boolean => df.isSignableByMetrum);
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda atlasot dokumenta informāciju');
        }
    }

    onAllChecked(checked: boolean): void {
        this.document.documentFiles.forEach((documentFile: DocumentFile): void => {
            if (!documentFile.isSignableByMetrum) {
                return;
            }

            this.onItemChecked(documentFile, checked);
        });
    }

    onItemChecked(documentFile: DocumentFile, checked: boolean): void {
        if (!documentFile.isSignableByMetrum) {
            return;
        }

        if (checked) {
            this.setOfCheckedFiles.add(documentFile);
        } else {
            this.setOfCheckedFiles.delete(documentFile);
        }
        this.refreshAllSelectedStatus();
    }

    refreshAllSelectedStatus(): void {
        const checkedFileIds = new Set([...this.setOfCheckedFiles].map((file) => file.id));

        this.allSelectedChecked = this.signableDocumentFiles.every((documentFile: DocumentFile): boolean => checkedFileIds.has(documentFile.id));

        this.allSelectedIndeterminate =
            this.signableDocumentFiles.some((documentFile: DocumentFile) => checkedFileIds.has(documentFile.id)) && !this.allSelectedChecked;
    }

    converDocumentFileSetToFileArray(documentFileSet: Set<DocumentFile>): FileModel[] {
        return [...documentFileSet].map((df: DocumentFile): FileModel => df.file);
    }

    userHasToSignFile(documentFile: DocumentFile): boolean {
        const userId: number = parseInt(this.authService.getUserId(), 10);
        if (!documentFile.documentFileSigners) {
            return false;
        }

        const documentFileSigner: DocumentFileSigner = documentFile.documentFileSigners.find(
            (dfs: DocumentFileSigner): boolean => dfs.signerUserId === userId,
        );
        if (!documentFileSigner) {
            return false;
        }

        if (documentFileSigner.signedTimestamp) {
            return false;
        }

        return true;
    }

    userIsFileSigner(documentFile: DocumentFile): boolean {
        const userId: number = parseInt(this.authService.getUserId(), 10);
        if (!documentFile.documentFileSigners) {
            return false;
        }

        const documentFileSigner: DocumentFileSigner = documentFile.documentFileSigners.find(
            (dfs: DocumentFileSigner): boolean => dfs.signerUserId === userId,
        );
        if (!documentFileSigner) {
            return false;
        }

        return true;
    }

    readonly sortByDocumentFileOriginalName = TableSorters.sortString<DocumentFile>((e) => e.file.originalName);
    readonly sortByDocumentFileClientSignedTimestamp = TableSorters.sortDate<DocumentFile>((e) => e.clientSignedTimestamp);
    readonly sortByDocumentFileMetrumSignedTimestamp = TableSorters.sortDate<DocumentFile>((e) => e.metrumSignedTimestamp);
    readonly sortByDocumentFileIsSignableByClient = TableSorters.sortBoolean<DocumentFile>((e) => e.isSignableByClient);
    readonly sortByDocumentFileIsSignableByMetrum = TableSorters.sortBoolean<DocumentFile>((e) => e.isSignableByMetrum);
}
